import { corsUrl, loadBitmap, type AssetCatalog } from './assets';
import { WING_SHOW_SCALE } from './character-sheet';
import { AURA_ARMA, LUZ_PES, type EquipLightFrames } from './equip-light-data';

/** Quadros por segundo das luzes: os clipes do legado tocam na taxa do palco do jogo (25), como o bater das asas. */
export const EQUIP_LIGHT_FPS = 25;

export type EquipLightKind = 'aura-arma' | 'luz-pes';

/** Folha de quadros de uma rodela criada no painel: um PNG em `custom/rodelas/sheets/<file>`. */
export interface CustomRodelaSheet {
  file: string;
  width: number;
  height: number;
  count: number;
}

/**
 * Rodela criada no painel (ADR 0035), ids 4 a 99. O painel publica o índice em `custom/rodelas/index.json`. Os quadros ficam nas folhas, da esquerda
 * para a direita e de cima para baixo, cada um numa célula de `width × height`. A aura toca na mesma taxa das outras luzes (`EQUIP_LIGHT_FPS`).
 */
export interface CustomRodela {
  id: number;
  name: string;
  frames: number;
  width: number;
  height: number;
  originX: number;
  originY: number;
  sheets: CustomRodelaSheet[];
}

export type CustomRodelas = ReadonlyMap<number, CustomRodela>;

const CUSTOM_DIR = 'custom/rodelas';

/** O índice das rodelas do painel. Sem índice (ainda não há rodela, ou o bucket não responde) devolve vazio: o jogo segue só com as do legado. */
export async function loadCustomRodelas(baseUrl: string, fetchImpl: typeof fetch = (...args) => fetch(...args)): Promise<CustomRodelas> {
  try {
    const response = await fetchImpl(corsUrl(`${baseUrl.replace(/\/+$/, '')}/${CUSTOM_DIR}/index.json`), { cache: 'no-cache' });
    if (!response.ok) return new Map();
    const body = (await response.json()) as { version?: number; rodelas?: CustomRodela[] };
    if (body.version !== 1 || !Array.isArray(body.rodelas)) return new Map();
    return new Map(body.rodelas.filter((r) => Number.isInteger(r.id) && r.id >= 4 && r.id <= 99 && r.frames > 0 && r.sheets?.length > 0).map((r) => [r.id, r]));
  } catch {
    return new Map();
  }
}

/** Os quadros de uma rodela do painel, no mesmo formato dos atlas (`loop/001`...), uma lista por folha. */
export function customRodelaFrames(rodela: CustomRodela): Record<string, RawFrame>[] {
  let n = 0;
  return rodela.sheets.map((sheet) => {
    const columns = Math.max(1, Math.floor(sheet.width / rodela.width));
    const frames: Record<string, RawFrame> = {};
    for (let i = 0; i < sheet.count; i++) {
      n += 1;
      frames[`loop/${String(n).padStart(3, '0')}`] = { frame: { x: (i % columns) * rodela.width, y: Math.floor(i / columns) * rodela.height, w: rodela.width, h: rodela.height }, spriteSourceSize: { x: 0, y: 0 } };
    }
    return frames;
  });
}

/** Mede a rodela do painel como uma luz do legado (`frames`, tamanho e origem). */
const customData = (r: CustomRodela): EquipLightFrames => ({ frames: r.frames, width: r.width, height: r.height, originX: r.originX, originY: r.originY });

export interface EquipLightRef {
  kind: EquipLightKind;
  tier: number;
}

/**
 * Quais luzes o brilho (`Nimbus`) pede, como `PlayerInfo.getHaveLight` e `getHaveCircle` do legado. O valor é `roupa × 100 + arma`, cada parte de 0 a 3:
 * a parte da roupa acende a luz nos pés (`SinpleLightAsset`) e a parte da arma acende a aura atrás do boneco (`CircleLightAsset`).
 */
export function nimbusLights(nimbus: number, custom: CustomRodelas = new Map()): EquipLightRef[] {
  if (!Number.isInteger(nimbus) || nimbus <= 0) return [];
  const lights: EquipLightRef[] = [];
  const weapon = nimbus % 100;
  const clothing = Math.floor(nimbus / 100);
  if (weapon in AURA_ARMA || custom.has(weapon)) lights.push({ kind: 'aura-arma', tier: weapon });
  if (clothing in LUZ_PES) lights.push({ kind: 'luz-pes', tier: clothing });
  return lights;
}

/**
 * Onde a origem de cada luz cai, no contêiner do boneco em pé, em px do símbolo (`ShowCharacter.callBack01` e `callBack02`): a aura em (47, 65), atrás de
 * tudo; a luz em (45, 126), nos pés. O contêiner usa a mesma escala da asa (`WING_SHOW_SCALE` sobre o quadro do corpo).
 */
const ANCHOR: Readonly<Record<EquipLightKind, readonly [number, number]>> = { 'aura-arma': [47, 65], 'luz-pes': [45, 126] };

const DATA: Readonly<Record<EquipLightKind, Readonly<Record<number, EquipLightFrames>>>> = { 'aura-arma': AURA_ARMA, 'luz-pes': LUZ_PES };

export interface RawFrame {
  frame: { x: number; y: number; w: number; h: number };
  spriteSourceSize?: { x: number; y: number };
}
interface LightArt {
  sheets: { bitmap: ImageBitmap; frames: Record<string, RawFrame> }[];
}

/**
 * Uma luz do boneco em pé, tocada em loop numa tela (`canvas`). `x` e `y` dizem onde o canto da tela cai no quadro do corpo de 250 px
 * (pode ser negativo: a aura passa da borda). Os quadros vêm de `atlas/luzes/<tipo>-<nível>`, compostos do SWF do legado por `tools/extrair-luzes-equipamento.mjs`.
 */
export class EquipLightPlayer {
  readonly canvas = document.createElement('canvas');
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly frameCount: number;

  private readonly index = new Map<string, { bitmap: ImageBitmap; raw: RawFrame }>();

  private constructor(
    private readonly ref: EquipLightRef,
    art: LightArt,
    data: EquipLightFrames,
  ) {
    const [ax, ay] = ANCHOR[ref.kind];
    this.x = (ax - data.originX) * WING_SHOW_SCALE;
    this.y = (ay - data.originY) * WING_SHOW_SCALE;
    this.width = data.width * WING_SHOW_SCALE;
    this.height = data.height * WING_SHOW_SCALE;
    this.frameCount = data.frames;
    this.canvas.width = Math.ceil(this.width);
    this.canvas.height = Math.ceil(this.height);
    for (const sheet of art.sheets) for (const [name, raw] of Object.entries(sheet.frames)) this.index.set(name, { bitmap: sheet.bitmap, raw });
  }

  /** A arte baixada de cada luz, compartilhada: cada boneco cria a própria tela, mas as folhas e os quadros são os mesmos. */
  private static readonly art = new Map<string, Promise<LightArt | null>>();

  /** `null` se a luz não existe, não tem arte no manifest ou a arte não baixou. Cada chamada devolve um player novo (uma tela própria). */
  static async load(assets: AssetCatalog, ref: EquipLightRef, custom: CustomRodelas = new Map()): Promise<EquipLightPlayer | null> {
    const builtin = DATA[ref.kind][ref.tier];
    const rodela = !builtin && ref.kind === 'aura-arma' ? custom.get(ref.tier) : undefined;
    if (!builtin && !rodela) return null;
    if (rodela) return EquipLightPlayer.loadCustom(assets, ref, rodela);
    const key = `${ref.kind}-${ref.tier}`;
    let art = EquipLightPlayer.art.get(key);
    if (!art) {
      art = EquipLightPlayer.download(assets, key).catch(() => null);
      EquipLightPlayer.art.set(key, art);
    }
    const loaded = await art;
    return loaded ? new EquipLightPlayer(ref, loaded, builtin!) : null;
  }

  /** A rodela do painel: baixa as folhas do bucket (`custom/rodelas/sheets`) e monta os quadros pelo índice. A chave leva o hash das folhas, então editar a rodela baixa a arte nova. */
  private static async loadCustom(assets: AssetCatalog, ref: EquipLightRef, rodela: CustomRodela): Promise<EquipLightPlayer | null> {
    const key = `custom-${rodela.id}-${rodela.sheets.map((s) => s.file.slice(0, 12)).join('-')}`;
    let art = EquipLightPlayer.art.get(key);
    if (!art) {
      const root = assets.baseUrl.replace(/\/+$/, '');
      const frames = customRodelaFrames(rodela);
      art = Promise.all(
        rodela.sheets.map(async (sheet, i) => {
          const bitmap = await loadBitmap(`${root}/${CUSTOM_DIR}/sheets/${sheet.file}`);
          return bitmap ? { bitmap, frames: frames[i]! } : null;
        }),
      )
        .then((sheets) => (sheets.every((s) => s !== null) ? ({ sheets } as LightArt) : null))
        .catch(() => null);
      EquipLightPlayer.art.set(key, art);
    }
    const loaded = await art;
    return loaded ? new EquipLightPlayer(ref, loaded, customData(rodela)) : null;
  }

  /** Os atlas de uma luz: uma página (`<nome>.json`) ou várias (`<nome>-0.json`, `<nome>-1.json`...), uma folha WebP cada. */
  private static async download(assets: AssetCatalog, key: string): Promise<LightArt | null> {
    const base = `atlas/luzes/${key}/${key}`;
    const pages: string[] = [];
    if (assets.has(`${base}.json`)) pages.push(`${base}.json`);
    else for (let i = 0; assets.has(`${base}-${i}.json`); i++) pages.push(`${base}-${i}.json`);
    if (pages.length === 0) return null;
    const sheets = await Promise.all(
      pages.map(async (json) => {
        const webp = json.replace(/\.json$/, '.webp');
        if (!assets.has(webp)) return null;
        const response = await fetch(corsUrl(assets.url(json)));
        if (!response.ok) return null;
        const atlas = (await response.json()) as { frames: Record<string, RawFrame> };
        const bitmap = await loadBitmap(assets.url(webp));
        return bitmap ? { bitmap, frames: atlas.frames } : null;
      }),
    );
    return sheets.every((s) => s !== null) ? { sheets: sheets as LightArt['sheets'] } : null;
  }

  /** Desenha o quadro `tick` da animação (conta de 0, em loop). */
  draw(tick: number): void {
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    const found = this.index.get(`loop/${String((tick % this.frameCount) + 1).padStart(3, '0')}`);
    if (!found) return;
    const { bitmap, raw } = found;
    const off = raw.spriteSourceSize ?? { x: 0, y: 0 };
    ctx.drawImage(bitmap, raw.frame.x, raw.frame.y, raw.frame.w, raw.frame.h, off.x * WING_SHOW_SCALE, off.y * WING_SHOW_SCALE, raw.frame.w * WING_SHOW_SCALE, raw.frame.h * WING_SHOW_SCALE);
  }

  get kind(): EquipLightKind {
    return this.ref.kind;
  }
}
